import React from "react";

import { useState, useRef, useEffect } from "react";

import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import { UploadCloud, FileIcon, X, Loader2 } from "lucide-react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import { Textarea } from "components/Graphs/TextArea";
import { FlatCard } from "components/UI-lib";
import { BeakerIcon } from "@heroicons/react/24/outline";

export const LifeCycleScript: React.FC<{
  script: string;
  onSetScript: (script: string) => void;
  lifecycleScriptTab: "upload" | "paste";
  setLifecycleScriptTab: (tab: "upload" | "paste") => void;
  lifecycleScriptFile: File | null;
  setLifecycleScriptFile: (file: File | null) => void;
  saveDescription: string;
  hideTitle?: boolean;
  experimentalFlag?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}> = ({
  script,
  onSetScript,
  lifecycleScriptTab,
  setLifecycleScriptTab,
  lifecycleScriptFile,
  setLifecycleScriptFile,
  saveDescription,
  hideTitle,
  experimentalFlag,
  onValidationChange,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local state for script content - initialize from parent script
  const [localScriptContent, setLocalScriptContent] = useState(script);

  // Validation state
  const [validationError, setValidationError] = useState<string>("");

  // Validation function
  const validateScript = (scriptContent: string): boolean => {
    if (!scriptContent.trim()) {
      setValidationError("");
      return true; // Empty script is valid (no validation needed)
    }

    if (!scriptContent.trim().startsWith("#!/bin/bash")) {
      setValidationError("Script must start with #!/bin/bash");
      return false;
    }

    setValidationError("");
    return true;
  };

  // Initialize local state based on current tab and whether we have a file or content
  useEffect(() => {
    if (lifecycleScriptFile) {
      // If we have a file, clear any script content (file takes precedence)
      setLocalScriptContent("");
    } else if (lifecycleScriptTab === "paste") {
      // If no file and we're on paste tab, show the script content
      setLocalScriptContent(script);
    } else {
      // If no file and on upload tab, clear content
      setLocalScriptContent("");
    }
  }, [script, lifecycleScriptTab, lifecycleScriptFile]);

  // Validate script when it changes from parent
  useEffect(() => {
    if (script) {
      const isValid = validateScript(script);
      onValidationChange?.(isValid);
    } else {
      // Empty script is valid
      setValidationError("");
      onValidationChange?.(true);
    }
  }, [script, onValidationChange]);

  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      setLifecycleScriptFile(selectedFile);
      setLocalScriptContent(""); // Clear pasted content locally
      // Read file content and update parent immediately
      readFileAndSubmit(selectedFile);
    }
  };

  const readFileAndSubmit = async (file: File) => {
    setIsLoading(true);
    try {
      const fileContent = await file.text();
      if (fileContent) {
        const isValid = validateScript(fileContent);
        onSetScript(fileContent); // Send file content to parent container
        onValidationChange?.(isValid); // Notify parent of validation state
      }
    } catch (error) {
      console.error("Error reading file:", error);
    }
    setIsLoading(false);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleRemoveFile = () => {
    setLifecycleScriptFile(null);
  };

  return (
    <FlatCard>
      <div className="flex flex-col mb-8">
        {!hideTitle && (
          <h2 className="text-lg font-medium text-primary">
            Run a Setup Script
          </h2>
        )}
        <p className="text-sm text-secondary">
          Add a script that will run after the environment is created and the VM
          is ready.
        </p>
      </div>
      <div>
        <Tabs
          value={lifecycleScriptTab}
          onValueChange={(value) =>
            setLifecycleScriptTab(value as "upload" | "paste")
          }
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">File Upload</TabsTrigger>
            <TabsTrigger value="paste">Paste Script</TabsTrigger>
          </TabsList>
          <TabsContent value="upload" className="pt-4">
            {lifecycleScriptFile ? (
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                <div className="flex items-center gap-3 overflow-hidden">
                  <FileIcon className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col overflow-hidden">
                    <span className="font-medium text-sm truncate">
                      {lifecycleScriptFile.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {(lifecycleScriptFile.size / 1024).toFixed(2)} KB
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleRemoveFile}
                  className="rounded-full"
                >
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ) : (
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`relative flex flex-col items-center justify-center w-full p-10 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isDragging
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
              >
                <UploadCloud className="w-12 h-12 text-muted-foreground" />
                <p className="mt-4 text-sm font-medium text-center">
                  Drag & drop your script here or click to browse
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Any text-based file is supported.
                </p>
                <Input
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  ref={fileInputRef}
                  type="file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={(e) =>
                    handleFileChange(e.target.files ? e.target.files[0] : null)
                  }
                  disabled={isLoading}
                />
              </div>
            )}
          </TabsContent>
          <TabsContent value="paste" className="pt-4">
            <Textarea
              placeholder="Paste your setup script here... (Must start with #!/bin/bash)"
              className="min-h-[200px] font-mono text-sm"
              value={localScriptContent}
              onChange={(e) => {
                setLocalScriptContent(e.target.value);
                setLifecycleScriptFile(null); // Clear file when typing
                const isValid = validateScript(e.target.value);
                onSetScript(e.target.value); // Update parent immediately
                onValidationChange?.(isValid); // Notify parent of validation state
              }}
              disabled={isLoading}
            />
          </TabsContent>
        </Tabs>

        {/* Validation Error Display */}
        {validationError && (
          <div className="mt-2 text-red-500 text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded">
            {validationError}
          </div>
        )}
      </div>
      <div>
        {!isLoading &&
          (lifecycleScriptFile || localScriptContent.trim()) &&
          saveDescription && (
            <div className="text-sm text-highlight mt-2">{saveDescription}</div>
          )}
        {/* <Button
          onClick={handleSubmit}
          disabled={(!file && !scriptContent.trim()) || isLoading}
          className="w-full mt-4"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading ? "Uploading..." : "Apply"}
        </Button> */}
      </div>
    </FlatCard>
  );
};
